import type { TechnicalPassportDimensions, TechnicalPassportElement } from './types'

export const sockDecorationTypes = [
  'lace_trim',
  'picot_edge',
  'scalloped_edge',
  'double_layered_ruffle',
  'fabric_loop_trim',
  'folded_over_cuff',
  'bow_detail',
  'embroidery_motif',
  'contrast_piping',
  'applique_patch',
  'bead_trim',
  'silicone_grip_dots',
] as const

export type SockDecorationType = typeof sockDecorationTypes[number]
export type SockDecorationPosition = 'top_cuff' | 'leg_front' | 'leg_back' | 'ankle_area' | 'heel_area' | 'toe_area' | 'sole_area' | 'instep_area'

const decorationTypeSet = new Set<string>(sockDecorationTypes)
const decorationCommentPattern = /\[\[sock-decoration:([a-z_]+)\]\]/i

export function isSockDecoration(value?: string | TechnicalPassportElement): boolean {
  const type = typeof value === 'string' ? value : value?.element_type
  return decorationTypeSet.has(type ?? '')
}

export function decorationTypeFromElement(element: TechnicalPassportElement): SockDecorationType | undefined {
  if (isSockDecoration(element.element_type)) return element.element_type as SockDecorationType
  const match = element.comment?.match(decorationCommentPattern)
  const type = match?.[1]
  return isSockDecoration(type) ? type as SockDecorationType : undefined
}

export function stripDecorationCommentMarker(comment?: string): string {
  return comment?.replace(decorationCommentPattern, '').trim() ?? ''
}

export function restoreDecorativeElement(element: TechnicalPassportElement): TechnicalPassportElement {
  const decorationType = decorationTypeFromElement(element)
  if (!decorationType) return element
  return {
    ...element,
    element_type: decorationType,
    comment: stripDecorationCommentMarker(element.comment),
  }
}

export function serializeDecorativeElement(element: TechnicalPassportElement): TechnicalPassportElement {
  const decorationType = decorationTypeFromElement(element)
  if (!decorationType) return element
  const comment = stripDecorationCommentMarker(element.comment)
  return {
    ...element,
    element_type: 'custom',
    comment: `${comment ? `${comment}\n` : ''}[[sock-decoration:${decorationType}]]`,
  }
}

const decorationPositionMap: Record<SockDecorationType, SockDecorationPosition[]> = {
  lace_trim: ['top_cuff', 'ankle_area', 'instep_area'],
  picot_edge: ['top_cuff', 'ankle_area'],
  scalloped_edge: ['top_cuff', 'ankle_area', 'toe_area'],
  double_layered_ruffle: ['top_cuff', 'ankle_area'],
  fabric_loop_trim: ['top_cuff', 'leg_front', 'leg_back'],
  folded_over_cuff: ['top_cuff'],
  bow_detail: ['top_cuff', 'leg_front', 'leg_back', 'ankle_area'],
  embroidery_motif: ['leg_front', 'leg_back', 'instep_area', 'heel_area', 'toe_area'],
  contrast_piping: ['top_cuff', 'ankle_area', 'heel_area', 'toe_area', 'sole_area', 'instep_area'],
  applique_patch: ['leg_front', 'leg_back', 'instep_area'],
  bead_trim: ['top_cuff', 'ankle_area', 'instep_area'],
  silicone_grip_dots: ['sole_area', 'heel_area'],
}

const defaultPositionMap: Record<SockDecorationType, SockDecorationPosition> = {
  lace_trim: 'top_cuff',
  picot_edge: 'top_cuff',
  scalloped_edge: 'top_cuff',
  double_layered_ruffle: 'top_cuff',
  fabric_loop_trim: 'top_cuff',
  folded_over_cuff: 'top_cuff',
  bow_detail: 'leg_front',
  embroidery_motif: 'leg_front',
  contrast_piping: 'toe_area',
  applique_patch: 'leg_front',
  bead_trim: 'top_cuff',
  silicone_grip_dots: 'sole_area',
}

export function decorationAllowedPositions(type?: string, hasLegZones = true): SockDecorationPosition[] {
  const positions = decorationPositionMap[type as SockDecorationType] ?? decorationPositionMap.lace_trim
  return hasLegZones ? positions : positions.filter((position) => !['leg_front', 'leg_back'].includes(position))
}

export function defaultDecorationPosition(type: SockDecorationType, hasLegZones = true): SockDecorationPosition {
  const preferred = defaultPositionMap[type]
  const allowed = decorationAllowedPositions(type, hasLegZones)
  return allowed.includes(preferred) ? preferred : allowed[0] ?? 'top_cuff'
}

export function decorationDefaults(
  type: SockDecorationType,
  dimensions: TechnicalPassportDimensions,
  hasLegZones = true,
): Pick<TechnicalPassportElement, 'position_code' | 'length_mm' | 'width_mm' | 'height_mm' | 'knit_type' | 'yarn_role' | 'pattern_type' | 'is_required'> {
  const span = dimensions.cuff_width_mm ?? 80
  const position_code = defaultDecorationPosition(type, hasLegZones)
  const shared = {
    position_code,
    length_mm: span,
    yarn_role: 'decorative',
    is_required: true,
  }
  switch (type) {
    case 'lace_trim': return { ...shared, width_mm: 12, height_mm: 10, knit_type: 'plain', pattern_type: 'waves' }
    case 'picot_edge': return { ...shared, width_mm: 8, height_mm: 5, knit_type: 'rib_1x1', pattern_type: 'dots' }
    case 'scalloped_edge': return { ...shared, width_mm: 14, height_mm: 8, knit_type: 'plain', pattern_type: 'waves' }
    case 'double_layered_ruffle': return { ...shared, width_mm: 16, height_mm: 14, knit_type: 'plain', pattern_type: 'waves' }
    case 'fabric_loop_trim': return { ...shared, length_mm: 18, width_mm: 10, height_mm: 16, knit_type: 'plain', pattern_type: 'rib' }
    case 'folded_over_cuff': return { ...shared, width_mm: span, height_mm: dimensions.cuff_height_mm ?? 24, knit_type: 'rib_1x1', pattern_type: 'rib' }
    case 'bow_detail': return { ...shared, length_mm: 28, width_mm: 18, height_mm: 16, knit_type: 'plain', pattern_type: 'waves' }
    case 'embroidery_motif': return { ...shared, length_mm: 34, width_mm: 10, height_mm: 22, knit_type: 'jacquard', pattern_type: 'dots' }
    case 'contrast_piping': return { ...shared, length_mm: position_code === 'toe_area' ? dimensions.toe_length_mm ?? 45 : span, width_mm: 8, height_mm: 6, knit_type: 'plain', pattern_type: 'rib' }
    case 'applique_patch': return { ...shared, length_mm: 32, width_mm: 18, height_mm: 24, knit_type: 'jacquard', pattern_type: 'dots' }
    case 'bead_trim': return { ...shared, width_mm: 10, height_mm: 7, knit_type: 'plain', pattern_type: 'dots' }
    case 'silicone_grip_dots': return { ...shared, length_mm: dimensions.foot_length_mm ?? 120, width_mm: 12, height_mm: 5, knit_type: 'plain', pattern_type: 'dots' }
  }
}
